/**
 * Color scheme preference: stored per browser, resolved against the system setting, and written onto
 * the document element as data-color-scheme for style.css to pick up.
 *
 * Mirrors the service in the other projects - three options with System first and default, System
 * meaning "nothing stored" rather than a stored value of its own, so a visitor who never chooses
 * keeps following their system for good.
 *
 * Timoty Web's equivalent hands every call a React state setter, because MUI needs the resolved theme
 * back in React. Here the resolved scheme only ever feeds CSS through the document attribute, so the
 * service owns it outright and the UI asks for the stored preference when it needs to show one.
 */

export const kStorageTheme = 'theme';

export const kThemeSystem = 'system';
export const kThemeDark = 'dark';
export const kThemeLight = 'light';

export const kDefaultTheme = kThemeSystem;
export const kSupportedThemes = [kThemeSystem, kThemeDark, kThemeLight];
/** The two that are written to storage. System is the absence of a stored value. */
export const kForcedThemes = [kThemeDark, kThemeLight];

const kColorSchemeAttribute = 'data-color-scheme';
const kDarkMediaQuery = '(prefers-color-scheme: dark)';

let systemThemeListener = null;

/**
 * Read the stored preference, falling back to System.
 * @returns {string}
 */
export function GetSelectedTheme () {
	//Storage throws rather than returning null in a locked-down browser, and that is a valid state to be in.
	let storedTheme = null;
	try {
		storedTheme = window.localStorage.getItem (kStorageTheme);
	} catch (e) {
		return kDefaultTheme;
	}

	return storedTheme !== null && kForcedThemes.includes (storedTheme) ? storedTheme : kDefaultTheme;
}

/**
 * Get the scheme the system is currently asking for.
 * @returns {string}
 */
export function GetSystemTheme () {
	return window.matchMedia (kDarkMediaQuery).matches ? kThemeDark : kThemeLight;
}

/**
 * Resolve a preference to the scheme that should actually render.
 * @param {string} theme Preference to resolve
 * @returns {string}
 */
export function ResolveTheme (theme) {
	return theme === kThemeSystem ? GetSystemTheme () : theme;
}

/**
 * Write the resolved scheme onto the document element. The same attribute the pre-paint script in
 * header.php sets, so this only ever confirms or replaces what is already there.
 * @param {string} resolvedTheme Scheme to apply, never System
 */
export function ApplyTheme (resolvedTheme) {
	document.documentElement.setAttribute (kColorSchemeAttribute, resolvedTheme);
}

/**
 * Follow the system scheme until told otherwise. Applies the current one immediately.
 * Does not touch storage - following the system is what an absent preference means.
 */
export function ListenTheme () {
	StopListeningTheme ();

	ApplyTheme (GetSystemTheme ());

	const mediaQuery = window.matchMedia (kDarkMediaQuery);

	systemThemeListener = event => {
		ApplyTheme (event.matches ? kThemeDark : kThemeLight);
	};

	//Safari only gained addEventListener on MediaQueryList in 14, and this is a public site.
	if (typeof (mediaQuery.addEventListener) !== 'undefined') {
		mediaQuery.addEventListener ('change', systemThemeListener);
	} else {
		mediaQuery.addListener (systemThemeListener);
	}
}

/**
 * Stop following the system scheme.
 */
export function StopListeningTheme () {
	if (systemThemeListener === null) {
		return;
	}

	const mediaQuery = window.matchMedia (kDarkMediaQuery);

	if (typeof (mediaQuery.removeEventListener) !== 'undefined') {
		mediaQuery.removeEventListener ('change', systemThemeListener);
	} else {
		mediaQuery.removeListener (systemThemeListener);
	}

	systemThemeListener = null;
}

/**
 * Change the preference, store it and apply the scheme it resolves to.
 * @param {string} theme One of kSupportedThemes
 */
export function ChangeTheme (theme) {
	if (!kSupportedThemes.includes (theme)) {
		throw new Error (`Unsupported theme: ${theme}`);
	}

	if (theme === kThemeSystem) {
		try {
			window.localStorage.removeItem (kStorageTheme);
		} catch (e) {
			//Nothing to clear if storage is unavailable; the system is followed either way.
		}

		ListenTheme ();
		return;
	}

	StopListeningTheme ();

	try {
		window.localStorage.setItem (kStorageTheme, theme);
	} catch (e) {
		//The choice still applies to this page; it just will not survive a reload.
	}

	ApplyTheme (theme);
}

/**
 * Apply the stored preference on boot.
 */
export function InitTheme () {
	const selectedTheme = GetSelectedTheme ();

	if (selectedTheme === kThemeSystem) {
		ListenTheme ();
		return;
	}

	ApplyTheme (selectedTheme);
}
